import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getStockDisponible, StockDisponibleResponse } from '@/services/api/new.funciones.service';

// Query key factory para stock reservado
export const reservedStockKeys = {
    all: ['reserved-stock'] as const,
    byFuncion: (funcionId: string) => [...reservedStockKeys.all, funcionId] as const,
};

/**
 * Hook para obtener stock reservado con TanStack Query.
 * - Cache automático con ttl de 10s
 * - Garbage collection a los 30s
 * - Deduplicación de requests automática
 * - Background refetch cuando los datos están stale
 */
export function useReservedStock(funcionId: string | null | undefined) {
    const { data, isLoading, isFetching } = useQuery({
        queryKey: reservedStockKeys.byFuncion(funcionId!),
        queryFn: () => getStockDisponible(funcionId!),
        enabled: !!funcionId,
        staleTime: 10 * 1000,      // 10s antes de considerar stale
        gcTime: 30 * 1000,         // 30s en cache antes de garbage collection
    });

    return {
        stock: data?.entradas ?? [],
        resumen: data?.resumen,
        loading: isLoading,                    // Primera carga (sin datos)
        refreshing: isFetching && !isLoading,  // Background refetch (con datos)
    };
}

/**
 * Invalida el cache para una función específica y dispara refetch.
 */
export function useInvalidateReservedStock() {
    const queryClient = useQueryClient();

    return (funcionId: string) => {
        queryClient.invalidateQueries({
            queryKey: reservedStockKeys.byFuncion(funcionId)
        });
    };
}

/**
 * Limpia todo el cache de stock reservado.
 */
export function useClearReservedStock() {
    const queryClient = useQueryClient();

    return () => {
        queryClient.invalidateQueries({
            queryKey: reservedStockKeys.all
        });
    };
}

/**
 * Prefetchea el stock de múltiples funciones en background.
 * Ideal para llamar cuando se carga un evento con varias funciones.
 */
export function usePrefetchReservedStock(funcionIds: string[]) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!funcionIds.length) return;

        Promise.all(
            funcionIds.map((funcionId) =>
                queryClient.prefetchQuery({
                    queryKey: reservedStockKeys.byFuncion(funcionId),
                    queryFn: () => getStockDisponible(funcionId),
                    staleTime: 10 * 1000,
                })
            )
        );
    }, [funcionIds, queryClient]);
}
